// Pending member approval counts for the admin-bucket firefly section.
//
// Mirrors `AdminPanel.tsx` refresh logic: access-status `pending` profiles
// plus approved profiles without an active membership row in the org.

import type { SupabaseClient } from "@supabase/supabase-js";

export type PendingMemberApprovalCounts = {
  accessPendingCount: number;
  rolePendingCount: number;
};

export function totalPendingMemberApprovals(
  counts: PendingMemberApprovalCounts,
): number {
  return counts.accessPendingCount + counts.rolePendingCount;
}

type MembershipRow = { user_id: string; organization_id: string; is_active: boolean };
type ProfileRow = { id: string };

export async function fetchPendingMemberApprovalCounts(
  supabase: SupabaseClient,
  orgId: string,
): Promise<PendingMemberApprovalCounts | null> {
  const [pendingRes, approvedRes, membersRes] = await Promise.all([
    supabase.rpc("admin_list_org_profiles", {
      p_org_id: orgId,
      p_access_status: "pending",
    }),
    supabase.rpc("admin_list_org_profiles", {
      p_org_id: orgId,
      p_access_status: "approved",
    }),
    supabase.rpc("admin_list_org_members", { p_org_id: orgId }),
  ]);

  if (pendingRes.error || approvedRes.error || membersRes.error) {
    return null;
  }

  const membershipKeys = new Set<string>();
  for (const row of (membersRes.data as MembershipRow[] | null) ?? []) {
    if (!row.is_active) continue;
    membershipKeys.add(`${row.user_id}:${row.organization_id}`);
  }

  const approvedProfiles = (approvedRes.data as ProfileRow[] | null) ?? [];
  const rolePendingCount = approvedProfiles.filter(
    (p) => !membershipKeys.has(`${p.id}:${orgId}`),
  ).length;

  return {
    accessPendingCount: ((pendingRes.data as unknown[] | null) ?? []).length,
    rolePendingCount,
  };
}
